/**
 * Global Amazon affiliate kill-switch + affiliate block markers.
 *
 * Env (set both in Vercel when toggling):
 * - AMAZON_AFFILIATES_ENABLED (server)
 * - VITE_AMAZON_AFFILIATES_ENABLED (client)
 *
 * Default: DISABLED (false) if unset — set both to "true" to show publicly again.
 *
 * Markdown convention:
 *   {{affiliate}}
 *   ... pitch + {{amazon:uuid}} ...
 *   {{/affiliate}}
 */

const AFFILIATE_BLOCK_RE = /\{\{affiliate\}\}[\s\S]*?\{\{\/affiliate\}\}/gi;
const AFFILIATE_TAG_RE = /\{\{\/?affiliate\}\}/gi;
const AMAZON_TOKEN_RE = /\{\{amazon:[^}]+\}\}/gi;
/** Any leftover CMS shortcode — never leave these in public HTML/text. */
const ANY_SHORTCODE_RE = /\{\{[\s\S]*?\}\}/g;

function readEnvFlag(): boolean | undefined {
  // Vite client
  try {
    const vite = (import.meta as ImportMeta & { env?: Record<string, string> }).env;
    if (vite && typeof vite.VITE_AMAZON_AFFILIATES_ENABLED === 'string') {
      const v = vite.VITE_AMAZON_AFFILIATES_ENABLED.trim().toLowerCase();
      if (v === 'false' || v === '0' || v === 'off') return false;
      if (v === 'true' || v === '1' || v === 'on') return true;
    }
  } catch {
    // not in Vite context
  }
  // Node / SSR
  if (typeof process !== 'undefined' && process.env) {
    const raw =
      process.env.AMAZON_AFFILIATES_ENABLED ?? process.env.VITE_AMAZON_AFFILIATES_ENABLED;
    if (typeof raw === 'string') {
      const v = raw.trim().toLowerCase();
      if (v === 'false' || v === '0' || v === 'off') return false;
      if (v === 'true' || v === '1' || v === 'on') return true;
    }
  }
  return undefined;
}

/** Whether Amazon affiliate UI/content should appear on the public site. Default: off. */
export function areAmazonAffiliatesEnabled(): boolean {
  return readEnvFlag() === true;
}

/**
 * Prepare markdown for public rendering:
 * - flag off: drop entire {{affiliate}}…{{/affiliate}} blocks, orphan tags, and {{amazon:…}}
 * - flag on: unwrap affiliate tags only (keep inner content)
 */
export function preparePublicMarkdown(text: string | null | undefined): string {
  if (!text) return '';
  if (!areAmazonAffiliatesEnabled()) {
    return text
      .replace(AFFILIATE_BLOCK_RE, '')
      .replace(AFFILIATE_TAG_RE, '')
      .replace(AMAZON_TOKEN_RE, '')
      .replace(/\n{3,}/g, '\n\n');
  }
  return text.replace(AFFILIATE_TAG_RE, '');
}

/**
 * Last-resort scrub: remove any remaining `{{…}}` shortcodes from public output.
 * Logs when matches are found so regressions are visible in server logs.
 */
export function scrubResidualShortcodes(text: string | null | undefined): string {
  if (!text) return '';
  ANY_SHORTCODE_RE.lastIndex = 0;
  if (!ANY_SHORTCODE_RE.test(text)) return text;
  ANY_SHORTCODE_RE.lastIndex = 0;
  const leftovers = text.match(ANY_SHORTCODE_RE) || [];
  console.error(
    `[affiliates] residual shortcodes scrubbed from public output: ${leftovers
      .slice(0, 8)
      .join(', ')}${leftovers.length > 8 ? '…' : ''}`
  );
  return text.replace(ANY_SHORTCODE_RE, '').replace(/\n{3,}/g, '\n\n');
}

/**
 * Returns residual shortcode matches (for tests / CI). Empty = clean.
 */
export function findPublicShortcodes(text: string | null | undefined): string[] {
  if (!text) return [];
  return text.match(ANY_SHORTCODE_RE) || [];
}

/**
 * Assert public HTML/text has no CMS shortcodes. Scrubs and returns cleaned text.
 * Throws if `throwOnMatch` is true (use in CI scripts).
 */
export function assertNoPublicShortcodes(
  text: string | null | undefined,
  options?: { throwOnMatch?: boolean; label?: string }
): string {
  const raw = text || '';
  const found = findPublicShortcodes(raw);
  if (found.length === 0) return raw;
  const label = options?.label || 'public output';
  const msg = `[affiliates] ${label} still contains shortcodes: ${found.slice(0, 8).join(', ')}`;
  if (options?.throwOnMatch) {
    throw new Error(msg);
  }
  console.error(msg);
  return scrubResidualShortcodes(raw);
}

/** Wrap selection (or empty cursor) in an affiliate block for the editor. */
export function wrapInAffiliateBlock(selected: string): string {
  const inner = selected.trim() ? selected : '\n\n';
  return `{{affiliate}}\n${inner}\n{{/affiliate}}`;
}
